import type { ChangeEvent } from "react";
import { Fragment, ReactElement, useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    Stack,
    CircularProgress,
    TextField,
    Dialog,
    FormControl,
    Select,
    MenuItem,
    SelectChangeEvent,
    Pagination,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    Chip,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "src/utils/useSnackbar";
import {
    useGetOrganizationTypes,
    useCreateOrganizationType,
    useUpdateOrganizationType,
    useDeleteOrganizationType,
} from "src/api/client-api/useOrganizationType";
import type {
    CreateOrganizationTypeRequest,
    UpdateOrganizationTypeRequest,
    OrganizationType,
} from "src/types/organizationType";
import { formatDate } from "src/utils/formatting";
import { FoodsIcon } from "@foodpilot/foods";
import { useNavigateSettingsParent } from "./useNavigateSettingsParent";

// Delete Confirmation Modal Component
const DeleteOrganizationTypeModal = ({
    open,
    onClose,
    onConfirm,
    isDeleting,
}: {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isDeleting: boolean;
}) => {
    const { t } = useTranslation();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    overflow: "hidden",
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px",
                    borderBottom: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E", marginBottom: "8px" }}>
                        {t("organizationType.deleteTitle", "Delete Organization Type")}
                    </Typography>
                    <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#50504D" }}>
                        {t(
                            "organizationType.deleteDescription",
                            "Are you sure you want to delete this organization type?",
                        )}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                    }}
                    onClick={onClose}
                >
                    <FoodsIcon icon="close" size={1} />
                </Box>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: "12px",
                    padding: "0px 24px 0px 32px",
                    height: "72px",
                    backgroundColor: "white",
                    borderTop: "1px solid #E7E6E4",
                }}
            >
                <Button
                    onClick={onClose}
                    disabled={isDeleting}
                    sx={{
                        width: "100px",
                        height: "40px",
                        borderRadius: "44px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        lineHeight: "1.5em",
                        backgroundColor: "transparent",
                        color: "#393A36",
                        border: "1px solid #E7E6E4",
                        "&:hover": {
                            backgroundColor: "#F5F5F5",
                        },
                    }}
                >
                    {t("organizationType.cancel", "Cancel")}
                </Button>
                <Button
                    onClick={onConfirm}
                    disabled={isDeleting}
                    sx={{
                        width: "100px",
                        height: "40px",
                        borderRadius: "44px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        lineHeight: "1.5em",
                        backgroundColor: "primary.main",
                        color: "white",
                        "&:hover": {
                            backgroundColor: "primary.dark",
                        },
                        "&:disabled": {
                            backgroundColor: "primary.main",
                            color: "white",
                            opacity: 0.6,
                        },
                    }}
                >
                    {isDeleting ?
                        <CircularProgress size={20} color="inherit" />
                    :   t("organizationType.delete", "Delete")}
                </Button>
            </Box>
        </Dialog>
    );
};

// Create/Edit Organization Type Modal Component
const OrganizationTypeModal = ({
    open,
    onClose,
    onSuccess,
    editingType,
}: {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    editingType?: OrganizationType | null;
}) => {
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const [name, setName] = useState("");
    const [isDefault, setIsDefault] = useState(false);

    const createOrganizationTypeMutation = useCreateOrganizationType();
    const updateOrganizationTypeMutation = useUpdateOrganizationType();

    const isCreating = createOrganizationTypeMutation.isLoading;
    const isUpdating = updateOrganizationTypeMutation.isLoading;
    const isLoading = isCreating || isUpdating;

    const isEditing = !!editingType;

    // Reset form when modal opens or editingType changes
    useEffect(() => {
        if (open) {
            if (editingType) {
                // Pre-fill form for editing
                setName(editingType.name || "");
                setIsDefault(editingType.isDefault || false);
            } else {
                // Reset form for creating
                setName("");
                setIsDefault(false);
            }
        }
    }, [open, editingType]);

    const handleSave = async () => {
        if (!name.trim()) {
            setSnackbarProps({
                message: t("organizationType.nameRequired", "Name is required"),
                type: "error",
            });
            return;
        }

        try {
            if (isEditing && editingType) {
                const updateData: UpdateOrganizationTypeRequest = {
                    name: name.trim(),
                    isDefault,
                };

                await updateOrganizationTypeMutation.mutateAsync({
                    id: editingType.id,
                    data: updateData,
                });

                setSnackbarProps({
                    message: t("organizationType.updateSuccess", "Organization type updated successfully"),
                    type: "success",
                });
            } else {
                const createData: CreateOrganizationTypeRequest = {
                    name: name.trim(),
                    isDefault,
                    isDeletable: true,
                };

                await createOrganizationTypeMutation.mutateAsync(createData);

                setSnackbarProps({
                    message: t("organizationType.createSuccess", "Organization type created successfully"),
                    type: "success",
                });
            }

            onSuccess();
            onClose();
        } catch (error) {
            setSnackbarProps({
                message:
                    isEditing ?
                        t("organizationType.updateError", "Failed to update organization type")
                    :   t("organizationType.createError", "Failed to create organization type"),
                type: "error",
            });
        }
    };

    const isFormValid = name.trim().length > 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px",
                    overflow: "hidden",
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "32px",
                    borderBottom: "1px solid #E7E6E4",
                    backgroundColor: "white",
                }}
            >
                <Box>
                    <Typography variant="h3" sx={{ lineHeight: "1.6em", color: "#1E1E1E", marginBottom: "8px" }}>
                        {isEditing ?
                            t("organizationType.editTitle", "Edit Organization Type")
                        :   t("organizationType.createTitle", "Create Organization Type")}
                    </Typography>
                    <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#50504D" }}>
                        {isEditing ?
                            t("organizationType.editDescription", "Update the parameters for your organization type.")
                        :   t("organizationType.createDescription", "Set the parameters for your organization type.")}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        width: "40px",
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                    }}
                    onClick={onClose}
                >
                    <FoodsIcon icon="close" size={1} />
                </Box>
            </Box>

            {/* Body */}
            <Box
                sx={{
                    padding: "32px",
                    backgroundColor: "white",
                }}
            >
                <Stack spacing={3}>
                    {/* Name Field */}
                    <Box>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            {t("organizationType.nameLabel", "Name")}:
                        </Typography>
                        <TextField
                            fullWidth
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t("organizationType.namePlaceholder", "Enter organization type name")}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "24px",
                                    height: "40px",
                                    backgroundColor: "white",
                                    "& fieldset": {
                                        borderColor: "#E7E6E4",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#D0D0D0",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: (theme) => theme.palette.primary[600],
                                    },
                                },
                                "& .MuiOutlinedInput-input": { fontSize: "0.875rem", padding: "0px 12px 0px 20px" },
                            }}
                        />
                    </Box>

                    {/* Default Checkbox */}
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isDefault}
                                    onChange={(e) => setIsDefault(e.target.checked)}
                                    disabled={isEditing && editingType?.isDefault}
                                    sx={{
                                        color: "#E7E6E4",
                                        "&.Mui-checked": {
                                            color: "primary.main",
                                        },
                                    }}
                                />
                            }
                            label={
                                <Typography variant="small-medium" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                                    {t("organizationType.isDefault", "Set as default")}
                                </Typography>
                            }
                        />
                    </Box>
                </Stack>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: "12px",
                    padding: "0px 24px 0px 32px",
                    height: "72px",
                    backgroundColor: "white",
                    borderTop: "1px solid #E7E6E4",
                }}
            >
                <Button
                    onClick={onClose}
                    disabled={isLoading}
                    sx={{
                        width: "100px",
                        height: "40px",
                        borderRadius: "44px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        lineHeight: "1.5em",
                        backgroundColor: "transparent",
                        color: "#393A36",
                        border: "1px solid #E7E6E4",
                        "&:hover": {
                            backgroundColor: "#F5F5F5",
                        },
                    }}
                >
                    {t("organizationType.cancel", "Cancel")}
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={!isFormValid || isLoading}
                    sx={(theme) => ({
                        width: "100px",
                        height: "40px",
                        borderRadius: "44px",
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: "0.875rem",
                        lineHeight: "1.5em",
                        background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                        color: "white !important",
                        boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                        "&:hover": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                            boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                        },
                        "&:disabled": {
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            opacity: 0.36,
                            color: "white !important",
                        },
                    })}
                >
                    {isLoading ?
                        <CircularProgress size={20} color="inherit" />
                    : isEditing ?
                        t("organizationType.update", "Update")
                    :   t("organizationType.save", "Save")}
                </Button>
            </Box>
        </Dialog>
    );
};

// Organization Types Table Component
const OrganizationTypesTable = ({
    organizationTypes,
    onEdit,
    onDelete,
}: {
    organizationTypes: OrganizationType[];
    onEdit: (id: number) => void;
    onDelete: (id: number) => void;
}) => {
    const { t } = useTranslation();

    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: "8px",
                overflow: "hidden",
                boxShadow: "none",
                border: "1px solid #E7E6E4",
            }}
        >
            <Table sx={{ "& .MuiTableCell-root": { border: "none" } }}>
                <TableHead>
                    <TableRow
                        sx={{
                            backgroundColor: "#FEFCFB",
                            "& .MuiTableCell-root": {
                                borderBottom: "1px solid #E7E6E4",
                                borderRight: "1px solid #E7E6E4",
                                padding: "0px 14px 0px 20px",
                                height: "48px",
                                "&:last-child": {
                                    borderRight: "none",
                                },
                            },
                        }}
                    >
                        <TableCell>
                            <Typography variant="small-medium" sx={{ lineHeight: "1.67em", color: "#767673" }}>
                                {t("organizationType.name", "Name")}
                            </Typography>
                        </TableCell>
                        <TableCell sx={{ width: "150px" }}>
                            <Typography variant="small-medium" sx={{ lineHeight: "1.67em", color: "#767673" }}>
                                {t("organizationType.isDefault", "Default")}
                            </Typography>
                        </TableCell>
                        <TableCell sx={{ width: "180px" }}>
                            <Typography variant="small-medium" sx={{ lineHeight: "1.67em", color: "#767673" }}>
                                {t("organizationType.lastModified", "Last Modified")}
                            </Typography>
                        </TableCell>
                        <TableCell
                            sx={{
                                width: "100px",
                                minWidth: "100px",
                                maxWidth: "100px",
                            }}
                        >
                            <Typography variant="small-medium" sx={{ lineHeight: "1.67em", color: "#767673" }}>
                                {t("organizationType.actions", "Actions")}
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {organizationTypes.map((organizationType, index) => (
                        <Fragment key={organizationType.id}>
                            <TableRow
                                sx={{
                                    "& .MuiTableCell-root": {
                                        borderRight: "1px solid #E7E6E4",
                                        padding: "0px 14px 0px 20px",
                                        height: "58px",
                                        "&:last-child": {
                                            borderRight: "none",
                                            padding: "0px 8px",
                                            width: "100px",
                                            minWidth: "100px",
                                            maxWidth: "100px",
                                        },
                                    },
                                    "&:hover": {
                                        backgroundColor: "#F9F9F8",
                                    },
                                }}
                            >
                                <TableCell
                                    sx={{
                                        padding: "0px 20px",
                                        height: "58px",
                                        borderBottom: "none",
                                    }}
                                >
                                    <Typography variant="small-medium" sx={{ lineHeight: "22px", color: "#1E1E1E" }}>
                                        {organizationType.name}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ borderBottom: "none" }}>
                                    {organizationType.isDefault && (
                                        <Chip
                                            label={t("organizationType.default", "Default")}
                                            variant="filled"
                                            sx={{
                                                backgroundColor: "#E8F5E8",
                                                color: "#2E7D32",
                                                fontSize: "0.75rem",
                                                fontWeight: 700,

                                                height: "24px",
                                                borderRadius: "12px",
                                            }}
                                        />
                                    )}
                                </TableCell>
                                <TableCell sx={{ borderBottom: "none" }}>
                                    <Typography variant="small-medium" sx={{ lineHeight: "22px", color: "#1E1E1E" }}>
                                        {formatDate(organizationType.updatedAt)}
                                    </Typography>
                                </TableCell>
                                <TableCell
                                    sx={{
                                        borderBottom: "none",
                                    }}
                                >
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <Box
                                            component="button"
                                            onClick={() => onEdit(organizationType.id)}
                                            disabled={organizationType.isDefault}
                                            sx={{
                                                width: "32px",
                                                height: "32px",
                                                borderRadius: "50%",
                                                border: "none",
                                                backgroundColor: "transparent",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                cursor: organizationType.isDefault ? "not-allowed" : "pointer",
                                                opacity: organizationType.isDefault ? 0.5 : 1,
                                                transition: "background-color 0.2s",
                                                "&:hover": {
                                                    backgroundColor:
                                                        organizationType.isDefault ? "transparent" : "#F5F5F5",
                                                },
                                            }}
                                        >
                                            <FoodsIcon icon="edit" size={1} />
                                        </Box>
                                        <Box
                                            component="button"
                                            onClick={() => onDelete(organizationType.id)}
                                            disabled={organizationType.isDefault || !organizationType.isDeletable}
                                            sx={{
                                                width: "32px",
                                                height: "32px",
                                                borderRadius: "50%",
                                                border: "none",
                                                backgroundColor: "transparent",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                cursor:
                                                    organizationType.isDefault || !organizationType.isDeletable ?
                                                        "not-allowed"
                                                    :   "pointer",
                                                opacity:
                                                    organizationType.isDefault || !organizationType.isDeletable ?
                                                        0.5
                                                    :   1,
                                                transition: "background-color 0.2s",
                                                "&:hover": {
                                                    backgroundColor:
                                                        organizationType.isDefault || !organizationType.isDeletable ?
                                                            "transparent"
                                                        :   "#F5F5F5",
                                                },
                                            }}
                                        >
                                            <FoodsIcon icon="delete" size={1} />
                                        </Box>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                            {/* Separator line */}
                            {index < organizationTypes.length - 1 && (
                                <TableRow>
                                    <TableCell colSpan={4} sx={{ padding: "0px 24px", height: "1px" }}>
                                        <Box
                                            sx={{
                                                height: "1px",
                                                backgroundColor: "#E7E6E4",
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                        </Fragment>
                    ))}
                </TableBody>
            </Table>
        </Paper>
    );
};

// Main Component
export const OrganizationTypeList = (): ReactElement => {
    const { t } = useTranslation();
    const navigateSettingsParent = useNavigateSettingsParent();
    const { setSnackbarProps } = useSnackbar();

    // Pagination state
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    // Delete dialog state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [typeToDelete, setTypeToDelete] = useState<number | null>(null);

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [editingType, setEditingType] = useState<OrganizationType | null>(null);

    // API hooks
    const { data: organizationTypes = [], isLoading, isError, error, refetch } = useGetOrganizationTypes();

    const deleteOrganizationTypeMutation = useDeleteOrganizationType();
    const isDeleting = deleteOrganizationTypeMutation.isLoading;

    // Calculate pagination
    const totalItems = organizationTypes.length;
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    const paginatedOrganizationTypes = organizationTypes.slice(startIndex, endIndex);

    const handleCreateOrganizationType = () => {
        setEditingType(null);
        setModalOpen(true);
    };

    const handleModalSuccess = () => {
        // Refetch the data to update the table
        refetch();
    };

    const handleEditOrganizationType = (id: number) => {
        const typeToEdit = organizationTypes.find((type) => type.id === id);
        if (typeToEdit) {
            setEditingType(typeToEdit);
            setModalOpen(true);
        }
    };

    const handleDeleteClick = (id: number) => {
        setTypeToDelete(id);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!typeToDelete) return;

        try {
            await deleteOrganizationTypeMutation.mutateAsync(typeToDelete);

            setSnackbarProps({
                message: t("organizationType.deleteSuccess", "Organization type deleted successfully"),
                type: "success",
            });

            setDeleteDialogOpen(false);
            setTypeToDelete(null);

            // Refetch the data to update the table
            refetch();
        } catch (error) {
            setSnackbarProps({
                message: t("organizationType.deleteError", "Failed to delete organization type"),
                type: "error",
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setTypeToDelete(null);
    };

    const handlePageChange = (_: ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleLimitChange = (event: SelectChangeEvent<number>) => {
        setLimit(Number(event.target.value));
        setPage(1); // Reset to first page when changing limit
    };

    const totalPages = Math.ceil(totalItems / limit);

    return (
        <Box sx={{ padding: "32px" }}>
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid #E7E6E4",
                    borderRadius: "16px",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "32px",
                        borderBottom: "1px solid #E7E6E4",
                        backgroundColor: "white",
                    }}
                >
                    <Box
                        sx={{
                            width: "40px",
                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                        }}
                        onClick={navigateSettingsParent}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "& svg": {
                                    strokeWidth: "2px",
                                    stroke: "currentColor",
                                },
                            }}
                        >
                            <FoodsIcon icon="arrowLeftShort" size={6} />
                        </Box>
                    </Box>
                    <Typography variant="number-inter" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                        {t("organizationType.title", "Organization Types")}
                    </Typography>
                </Box>

                {/* Create button section */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        padding: "24px 32px 24px",
                        gap: "12px",
                    }}
                >
                    <Button
                        variant="contained"
                        startIcon={<FoodsIcon icon="plus" size={2} />}
                        onClick={handleCreateOrganizationType}
                        sx={(theme) => ({
                            borderRadius: "44px",
                            padding: "0px 20px",
                            height: "40px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                            color: "white !important",
                            boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                            "&:hover": {
                                background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                color: "white !important",
                            },
                            "&:disabled": {
                                background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                opacity: 0.36,
                                color: "white !important",
                            },
                        })}
                    >
                        {t("organizationType.create", "Create Organization Type")}
                    </Button>
                </Box>

                {/* Content area */}
                <Box sx={{ padding: "0px 32px 24px" }}>
                    {isLoading ?
                        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                            <CircularProgress />
                        </Box>
                    : isError ?
                        <Box sx={{ my: 2, p: 2, bgcolor: "error.light", borderRadius: 1 }}>
                            <Typography color="error">
                                {t("organizationType.error", "Error")}:{" "}
                                {(error as Error)?.message ||
                                    t("organizationType.unknownError", "An unknown error occurred")}
                            </Typography>
                        </Box>
                    :   <>
                            <OrganizationTypesTable
                                organizationTypes={paginatedOrganizationTypes}
                                onEdit={handleEditOrganizationType}
                                onDelete={handleDeleteClick}
                            />

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ mt: 3 }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography variant="body2">
                                            {t("organizationType.rowsPerPage", "Rows per page")}:
                                        </Typography>
                                        <FormControl
                                            size="small"
                                            sx={{
                                                minWidth: 80,
                                                "& .MuiOutlinedInput-root": {
                                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: (theme) => theme.palette.primary[600],
                                                    },
                                                },
                                            }}
                                        >
                                            <Select value={limit} onChange={handleLimitChange}>
                                                <MenuItem value={5}>5</MenuItem>
                                                <MenuItem value={10}>10</MenuItem>
                                                <MenuItem value={25}>25</MenuItem>
                                                <MenuItem value={50}>50</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>

                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={handlePageChange}
                                        showFirstButton
                                        showLastButton
                                        sx={{
                                            "& .MuiPaginationItem-root": {
                                                color: "#393A36",
                                            },
                                            "& .MuiPaginationItem-root.Mui-selected": {
                                                backgroundColor: (theme) => theme.palette.primary[600],
                                                color: "white",
                                                "&:hover": {
                                                    backgroundColor: (theme) => theme.palette.primary[700],
                                                },
                                            },
                                        }}
                                    />

                                    <Typography variant="body2">
                                        {t("organizationType.showing", "Showing")}{" "}
                                        {Math.min((page - 1) * limit + 1, totalItems)}-
                                        {Math.min(page * limit, totalItems)} {t("organizationType.of", "of")}{" "}
                                        {totalItems}
                                    </Typography>
                                </Stack>
                            )}
                        </>
                    }
                </Box>
            </Paper>

            {/* Organization Type Modal */}
            <OrganizationTypeModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSuccess={handleModalSuccess}
                editingType={editingType}
            />

            {/* Delete Confirmation Modal */}
            <DeleteOrganizationTypeModal
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                isDeleting={isDeleting}
            />
        </Box>
    );
};
